import { motion, AnimatePresence, useInView } from 'framer-motion';
import { StackedContent, MotionImage, useSanitizedTranslation, TextBlock } from 'ui';
import { useState, useRef, useEffect } from 'react';

export interface Testimonial {
  copy: string;
  name: string;
}

export interface TestimonialsProps {
  bg: string;
  image: string;
  testimonials: Testimonial[];
  title: string;
}

export function Testimonials({ bg = 'white', image, testimonials, title }: TestimonialsProps) {
  const { t } = useSanitizedTranslation();

  const [currentItem, setCurrentItem] = useState(0);

  const changeItem = (index: number) => {
    const newItem = index < testimonials.length ? index : 0;
    setCurrentItem(newItem);
  };

  const handleSelect = (index: number) => {
    setCurrentItem(index);
  };

  useEffect(() => {
    const newItem = currentItem + 1;
    const timeout = setTimeout(() => {
      changeItem(newItem);
    }, 4000);
    return () => clearTimeout(timeout);
  }, [currentItem]);

  return (
    <motion.section className="relative min-h-[800px] w-full py-12 sm:min-h-[900px] md:min-h-[1000px] lg:py-32" style={{ background: bg }}>
      <div className="wrapper max-w-[1100px] text-xumoDarkPlum">
        <AnimatePresence mode="wait">
          <motion.div
            className="flex w-full flex-col items-center gap-10 text-center "
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5 }}
          >
            {/* Title */}
            <p className="weight-500 text-[35px] tracking-tight" data-i18n>
              {t(title)}
            </p>

            {/* Copy */}
            <motion.p
              key={currentItem}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.75 }}
              className="type-heading-alt min-h-[200px]"
              data-i18n
            >
              “{t(testimonials[currentItem].copy)}”
            </motion.p>

            {/* Name and buttons */}
            <div className="flex w-full flex-col items-center justify-center">
              <motion.p
                key={currentItem}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.75 }}
                data-i18n
              >
                {t(testimonials[currentItem].name)}
              </motion.p>

              {/* Scroll indicators */}
              <div className="z-50 mb-4 mt-8 flex justify-center gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelect(index)}
                    className={`h-3 w-3 rounded-full transition-all duration-300 ease-linear ${
                      currentItem === index ? 'w-8 bg-xumoDarkPlum' : 'bg-xumoDarkPlum/30'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Swirl */}
      <div className="absolute bottom-[100px] flex w-full justify-center md:bottom-[200px] lg:bottom-[0px]">
        <div className="relative h-[250px] w-full max-w-[2040px] md:h-[900px]">
          {/* Opacity fade at left and right edges on ultrawide screens */}
          <div
            style={{
              display: 'none',
            }}
            className="absolute -top-[250px] z-20 h-[700px] w-full bg-transparent"
            aria-hidden
          >
            <style jsx>{`
              @media (min-width: 2040px) {
                div[aria-hidden] {
                  display: block !important;
                }
              }
            `}</style>
            <div className="pointer-events-none absolute left-0 top-0 h-full w-[100px] bg-gradient-to-r from-white via-white/40 to-transparent" />

            <div className="pointer-events-none absolute right-0 top-0 h-full w-[100px] bg-gradient-to-l from-white via-white/40 to-transparent" />
          </div>

          {/* Swirl image */}
          <MotionImage src="/static/images/refresh/2025/about-swirl-3.png" priority fill alt="" style={{ objectFit: 'cover', objectPosition: 'center' }} />
        </div>
      </div>
    </motion.section>
  );
}
